import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import type { AppEpreuve, AppObjectiveTemplate } from './objective-types';

type EpreuveRow = Database['public']['Tables']['epreuves']['Row'];

function mapEpreuveRow(
  row: EpreuveRow & { epreuve_exercises?: { count: number }[] },
): AppEpreuve & { exerciseCount: number } {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? null,
    subject: row.subject as SubjectSlug | null,
    level: row.level as SchoolLevel,
    examType: row.exam_type ?? null,
    examYear: row.exam_year ?? null,
    sourceFileUrl: row.source_file_url ?? null,
    ocr_status: row.ocr_status as AppEpreuve['ocr_status'],
    status: row.status as AppEpreuve['status'],
    exerciseCount: row.epreuve_exercises?.[0]?.count ?? 0,
  };
}

export async function getEpreuvesForObjective(
  supabase: SupabaseClient<Database>,
  template: AppObjectiveTemplate,
): Promise<AppEpreuve[]> {
  const { data, error } = await supabase
    .from('epreuves')
    .select('*, epreuve_exercises(count)')
    .eq('status', 'published')
    .order('exam_year', { ascending: false });

  if (error) throw error;

  return (data ?? [])
    .filter((row) => {
      if (
        template.allowedLevels.length > 0 &&
        !template.allowedLevels.includes(row.level as SchoolLevel)
      ) {
        return false;
      }
      if (template.subject && row.subject !== template.subject) {
        return false;
      }
      return true;
    })
    .map((row) =>
      mapEpreuveRow(row as unknown as EpreuveRow & { epreuve_exercises?: { count: number }[] }),
    );
}

// ─── Statistiques de réussite (réussite % + temps, global et par exercice) ───

/** Tentative complétée brute, telle que lue depuis `attempts`. */
export interface EpreuveAttemptRow {
  exercise_id: string;
  score: number | null;
  started_at: string;
  completed_at: string | null;
}

export interface EpreuveExerciseStat {
  /** Meilleur score obtenu sur l'exercice (0-100). */
  score: number;
  /** Durée de la tentative correspondant au meilleur score (ms, ≥ 0). */
  timeMs: number;
}

export interface EpreuveStats {
  /** Stat par exercice (clé = exerciseId), uniquement les exercices tentés. */
  byExercise: Record<string, EpreuveExerciseStat>;
  /** Nombre d'exercices avec au moins une tentative complétée. */
  completedCount: number;
  /** Réussite globale = moyenne arrondie des meilleurs scores (0-100). */
  avgScore: number;
  /** Temps total = somme des temps des meilleures tentatives (ms). */
  totalTimeMs: number;
}

function attemptDurationMs(a: EpreuveAttemptRow): number {
  if (!a.completed_at) return 0;
  return Math.max(0, new Date(a.completed_at).getTime() - new Date(a.started_at).getTime());
}

/**
 * Réduit les tentatives complétées en stats par exercice + agrégat global.
 *
 * Par exercice on retient la **meilleure** tentative (score le plus haut ;
 * égalité → la plus récente par `completed_at`). La réussite globale est la
 * moyenne de ces meilleurs scores sur les exercices tentés ; le temps global
 * la somme des temps de ces mêmes tentatives.
 */
export function computeEpreuveStats(attempts: EpreuveAttemptRow[]): EpreuveStats {
  const best = new Map<string, EpreuveAttemptRow>();
  for (const a of attempts) {
    const current = best.get(a.exercise_id);
    if (!current) {
      best.set(a.exercise_id, a);
      continue;
    }
    const aScore = a.score ?? 0;
    const curScore = current.score ?? 0;
    const isBetter =
      aScore > curScore ||
      (aScore === curScore &&
        !!a.completed_at &&
        (!current.completed_at || a.completed_at > current.completed_at));
    if (isBetter) best.set(a.exercise_id, a);
  }

  const byExercise: Record<string, EpreuveExerciseStat> = {};
  let scoreSum = 0;
  let totalTimeMs = 0;
  for (const [exerciseId, a] of best) {
    const score = a.score ?? 0;
    const timeMs = attemptDurationMs(a);
    byExercise[exerciseId] = { score, timeMs };
    scoreSum += score;
    totalTimeMs += timeMs;
  }

  const completedCount = best.size;
  const avgScore = completedCount > 0 ? Math.round(scoreSum / completedCount) : 0;
  return { byExercise, completedCount, avgScore, totalTimeMs };
}

// ─── Progression par épreuve (exos faits / total) + tri par récence ──────────

/** Lien épreuve ⇄ exercice (table `epreuve_exercises`). */
export interface EpreuveLink {
  epreuve_id: string;
  exercise_id: string;
}

/** Tentative complétée d'un élève sur un exercice d'épreuve. */
export interface EpreuveExerciseAttempt {
  exercise_id: string;
  completed_at: string | null;
}

export interface EpreuveProgress {
  /** Exercices avec au moins une tentative complétée. */
  done: number;
  /** Total d'exercices de l'épreuve. */
  total: number;
  /** Dernière tentative complétée (ISO) sur un exo de l'épreuve, ou null. */
  lastAttemptAt: string | null;
}

/**
 * Calcule, par épreuve, le nombre d'exercices faits / total et la date de la
 * dernière révision. Un exercice n'est compté qu'une fois même s'il a plusieurs
 * tentatives ; `lastAttemptAt` est le max des `completed_at` de l'épreuve.
 */
export function computeEpreuveProgressMap(
  links: EpreuveLink[],
  completed: EpreuveExerciseAttempt[],
): Record<string, EpreuveProgress> {
  // exercise_id → dernière complétion (max completed_at).
  const lastByExercise = new Map<string, string | null>();
  for (const a of completed) {
    const prev = lastByExercise.get(a.exercise_id);
    if (prev === undefined) {
      lastByExercise.set(a.exercise_id, a.completed_at ?? null);
    } else if (a.completed_at && (!prev || a.completed_at > prev)) {
      lastByExercise.set(a.exercise_id, a.completed_at);
    }
  }

  const map: Record<string, EpreuveProgress> = {};
  for (const { epreuve_id, exercise_id } of links) {
    const entry = (map[epreuve_id] ??= { done: 0, total: 0, lastAttemptAt: null });
    entry.total += 1;
    if (lastByExercise.has(exercise_id)) {
      entry.done += 1;
      const at = lastByExercise.get(exercise_id) ?? null;
      if (at && (!entry.lastAttemptAt || at > entry.lastAttemptAt)) {
        entry.lastAttemptAt = at;
      }
    }
  }
  return map;
}

/**
 * Trie les épreuves : les plus récemment révisées d'abord (par `lastAttemptAt`
 * décroissant), puis les non-révisées en conservant leur ordre d'entrée (tri
 * stable). Ne mute pas l'entrée.
 */
export function sortEpreuvesByRecency<T extends { id: string }>(
  epreuves: T[],
  progress: Record<string, EpreuveProgress>,
): T[] {
  return [...epreuves].sort((a, b) => {
    const aAt = progress[a.id]?.lastAttemptAt ?? null;
    const bAt = progress[b.id]?.lastAttemptAt ?? null;
    if (aAt && bAt) return aAt < bAt ? 1 : aAt > bAt ? -1 : 0;
    if (aAt) return -1;
    if (bAt) return 1;
    return 0;
  });
}

export interface EpreuveWithExercises {
  id: string;
  title: string;
  description: string | null;
  examType: string | null;
  examYear: number | null;
  exercises: EpreuveExerciseItem[];
}

export interface EpreuveExerciseItem {
  exerciseId: string;
  displayOrder: number;
  title: string;
  difficulty: 'easy' | 'medium' | 'hard';
  estimatedMinutes: number | null;
  chapterId: string | null;
  chapterSlug: string | null;
  chapterSubject: string | null;
  chapterLevel: string | null;
}

/**
 * Cible de navigation pour lancer un exercice d'épreuve.
 *
 * - Exercice rattaché à un chapitre → route chapitrée (cover/cours/partage).
 * - Exercice d'épreuve sans chapitre (pipeline PDF, `chapter_id = null`) →
 *   route lecteur directe par id.
 *
 * Retourne `null` si l'exercice n'a pas d'id (cas dégradé, non jouable).
 */
export type EpreuveExerciseTarget =
  | {
      to: '/learn/$subject/$level/$chapter/$exercise';
      params: { subject: string; level: string; chapter: string; exercise: string };
    }
  | { to: '/learn/exercise/$exercise'; params: { exercise: string } };

export function epreuveExerciseTarget(item: EpreuveExerciseItem): EpreuveExerciseTarget | null {
  if (!item.exerciseId) return null;
  if (item.chapterSlug && item.chapterSubject && item.chapterLevel) {
    return {
      to: '/learn/$subject/$level/$chapter/$exercise',
      params: {
        subject: item.chapterSubject,
        level: item.chapterLevel,
        chapter: item.chapterSlug,
        exercise: item.exerciseId,
      },
    };
  }
  return { to: '/learn/exercise/$exercise', params: { exercise: item.exerciseId } };
}

export async function getEpreuveWithExercises(
  supabase: SupabaseClient<Database>,
  epreuveId: string,
): Promise<EpreuveWithExercises | null> {
  const { data, error } = await supabase
    .from('epreuves')
    .select(
      `id, title, description, exam_type, exam_year,
       epreuve_exercises(
         exercise_id,
         display_order,
         exercises(id, title, difficulty, estimated_minutes, chapter_id,
           chapters(slug, subject, level)
         )
       )`,
    )
    .eq('id', epreuveId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  type EERow = {
    exercise_id: string;
    display_order: number;
    exercises: {
      id: string;
      title: string;
      difficulty: string;
      estimated_minutes: number | null;
      chapter_id: string | null;
      chapters: { slug: string; subject: string; level: string } | null;
    } | null;
  };

  const exercises: EpreuveExerciseItem[] = ((data.epreuve_exercises as unknown as EERow[]) ?? [])
    .sort((a, b) => a.display_order - b.display_order)
    .map((ee) => {
      const ex = ee.exercises;
      const ch = ex?.chapters ?? null;
      return {
        exerciseId: ee.exercise_id,
        displayOrder: ee.display_order,
        title: ex?.title ?? '',
        difficulty: (ex?.difficulty ?? 'medium') as EpreuveExerciseItem['difficulty'],
        estimatedMinutes: ex?.estimated_minutes ?? null,
        chapterId: ex?.chapter_id ?? null,
        chapterSlug: ch?.slug ?? null,
        chapterSubject: ch?.subject ?? null,
        chapterLevel: ch?.level ?? null,
      };
    });

  return {
    id: data.id,
    title: data.title,
    description: data.description ?? null,
    examType: data.exam_type ?? null,
    examYear: data.exam_year ?? null,
    exercises,
  };
}
